/**
 * Client-side TCPA calling-hours check — for INSTANT queue flagging (no network
 * wait). The backend `/communication/calling-hours/check` remains authoritative
 * and reconciles edge cases (multi-zone states, DB appointment exemptions) a
 * moment later; this just paints the after-hours badge immediately.
 *
 * Multi-zone states map to their MAJORITY zone — good enough for an instant
 * estimate; the backend corrects the rare edge lead.
 */

const STATE_TZ: Record<string, string> = {
  AL: "America/Chicago", AK: "America/Anchorage", AZ: "America/Phoenix",
  AR: "America/Chicago", CA: "America/Los_Angeles", CO: "America/Denver",
  CT: "America/New_York", DE: "America/New_York", DC: "America/New_York",
  FL: "America/New_York", GA: "America/New_York", HI: "Pacific/Honolulu",
  ID: "America/Boise", IL: "America/Chicago", IN: "America/Indiana/Indianapolis",
  IA: "America/Chicago", KS: "America/Chicago", KY: "America/New_York",
  LA: "America/Chicago", ME: "America/New_York", MD: "America/New_York",
  MA: "America/New_York", MI: "America/Detroit", MN: "America/Chicago",
  MS: "America/Chicago", MO: "America/Chicago", MT: "America/Denver",
  NE: "America/Chicago", NV: "America/Los_Angeles", NH: "America/New_York",
  NJ: "America/New_York", NM: "America/Denver", NY: "America/New_York",
  NC: "America/New_York", ND: "America/Chicago", OH: "America/New_York",
  OK: "America/Chicago", OR: "America/Los_Angeles", PA: "America/New_York",
  RI: "America/New_York", SC: "America/New_York", SD: "America/Chicago",
  TN: "America/Chicago", TX: "America/Chicago", UT: "America/Denver",
  VT: "America/New_York", VA: "America/New_York", WA: "America/Los_Angeles",
  WV: "America/New_York", WI: "America/Chicago", WY: "America/Denver",
  PR: "America/Puerto_Rico",
};

const NAME_TO_ABBR: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL",
  indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
  maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI",
  minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC",
  "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR",
  pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY", "puerto rico": "PR",
};

const tzForState = (state?: string | null): string | null => {
  if (!state) return null;
  const raw = state.trim();
  if (raw.length === 2) return STATE_TZ[raw.toUpperCase()] ?? null;
  const abbr = NAME_TO_ABBR[raw.toLowerCase()];
  return abbr ? STATE_TZ[abbr] : null;
};

/** Local hour (0–23) in the given IANA timezone right now, or null if it fails. */
const hourInTz = (tz: string): number | null => {
  try {
    const h = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      hour12: false,
    }).format(new Date());
    const n = parseInt(h, 10);
    return Number.isFinite(n) ? n % 24 : null;
  } catch {
    return null;
  }
};

/** TCPA window is 8:00am–9:00pm local. Outside = hour < 8 or hour >= 21. */
export const isStateOutsideCallingHours = (state?: string | null): boolean | null => {
  const tz = tzForState(state);
  if (!tz) return null; // unknown state → let the backend decide
  const hour = hourInTz(tz);
  if (hour == null) return null;
  return hour < 8 || hour >= 21;
};

const EXEMPT = /sold|underwrit|appointment/i;

/**
 * Instant per-lead after-hours decision for the queue badge. Returns true only
 * when we're confident (known state, outside window, not an exempt status).
 * Unknown states return false here so we don't over-flag — the backend check
 * catches anything this misses.
 */
export const isLeadOutsideCallingHours = (lead: {
  state?: string | null;
  leadStat?: string | null;
  status?: string | null;
}): boolean => {
  if (EXEMPT.test(lead?.leadStat || "") || EXEMPT.test(lead?.status || "")) return false;
  return isStateOutsideCallingHours(lead?.state) === true;
};
